import Keycloak from 'keycloak-js';

interface RuntimeConfig {
  keycloakUrl: string;
  keycloakRealm: string;
  keycloakClientId: string;
  opsEnabled: boolean;
}

let keycloak: Keycloak | null = null;
let initPromise: Promise<boolean> | null = null;
let runtimeConfig: RuntimeConfig | null = null;

async function loadConfig(): Promise<RuntimeConfig> {
  const res = await fetch('/config.json', { cache: 'no-store' });
  if (!res.ok) throw new Error(`failed to load /config.json: ${res.status}`);
  const cfg = (await res.json()) as Partial<RuntimeConfig>;
  if (!cfg.keycloakUrl || !cfg.keycloakRealm || !cfg.keycloakClientId) {
    throw new Error('missing fields in /config.json');
  }
  const full: RuntimeConfig = {
    ...cfg,
    opsEnabled: cfg.opsEnabled === true,
  } as RuntimeConfig;
  runtimeConfig = full;
  return full;
}

// isOpsEnabled reflects whether THIS server booted with the ops agent
// configured — independent of the viewer's role. useCan('ops','view') is
// role-derived and true for every admin regardless of server config, so the
// rail entry and the /ops routes both need this separate, server-sourced
// signal to stay hidden when the agent isn't actually running.
export function isOpsEnabled(): boolean {
  return runtimeConfig?.opsEnabled === true;
}

export function getKeycloak(): Keycloak {
  if (!keycloak)
    throw new Error('keycloak not initialized; call initKeycloak first');
  return keycloak;
}

export function initKeycloak(): Promise<boolean> {
  if (!initPromise) {
    initPromise = (async () => {
      const cfg = await loadConfig();
      keycloak = new Keycloak({
        url: cfg.keycloakUrl,
        realm: cfg.keycloakRealm,
        clientId: cfg.keycloakClientId,
      });
      return keycloak.init({
        onLoad: 'login-required',
        pkceMethod: 'S256',
        checkLoginIframe: false,
        // A URL inteira, não a origem: com onLoad 'login-required' todo F5
        // passa pelo Keycloak, e devolver na origem descarta o caminho — o
        // usuário recarrega /ops e reaparece em /overview.
        redirectUri: window.location.href,
        enableLogging: true,
      });
    })();
  }
  return initPromise;
}

export function logout(): void {
  const k = getKeycloak();
  k.logout({ redirectUri: window.location.origin });
}

export async function getToken(): Promise<string> {
  const k = getKeycloak();
  try {
    await k.updateToken(30);
  } catch {
    // Mesma razão do init: relogar não pode custar a página em que a pessoa
    // estava. O logout segue indo para a origem, que ali é o destino certo.
    k.login({ redirectUri: window.location.href });
    throw new Error('keycloak token refresh failed');
  }
  return k.token ?? '';
}

export function setupTokenRefresh(intervalMs = 30_000): () => void {
  const id = setInterval(() => {
    const k = getKeycloak();
    k.updateToken(60).catch(() =>
      k.login({ redirectUri: window.location.origin })
    );
  }, intervalMs);
  return () => clearInterval(id);
}
